import { Box, Flex, Grid, Link, PopoverBody, PopoverContent, PopoverTrigger, Text, VStack, chakra, useColorModeValue, StackDivider } from '@chakra-ui/react';
import React from 'react';

import type { NavGroupItem, NavItem } from 'types/client/navigation';

import { route } from 'nextjs-routes';

import { isInternalItem } from 'lib/hooks/useNavItems';
import getDefaultTransitionProps from 'theme/utils/getDefaultTransitionProps';
import Popover from 'ui/shared/chakra/Popover';
import IconSvg, { type IconName } from 'ui/shared/IconSvg';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import LinkInternal from 'ui/shared/links/LinkInternal';

import LightningLabel from '../LightningLabel';
import { checkRouteHighlight } from '../utils';
import NavLink from './NavLink';

const MEGA_SECTION_TITLES = [ 'Tools', 'Explore', 'Services' ] as const;
const BETA_LABEL_ITEMS = new Set([ 'Input Data Messages (IDM)', 'Blockscan Chat' ]);

// Top bar + main nav (56px) — mega menu starts just below header so logo/nav stay fully visible (no overlap)
const HEADER_HEIGHT_PX = 105;

type MegaMenuItem = NavItem | (NavItem & { icon?: IconName });

function MegaMenuRow({ item }: { item: MegaMenuItem }) {
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const isInternal = isInternalItem(item);
  const href = isInternal ? route(item.nextRoute) : item.url;
  const isActive = isInternal && item.isActive;
  const showBeta = BETA_LABEL_ITEMS.has(item.text);
  const iconName = 'icon' in item ? item.icon : undefined;

  const linkProps = {
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: 3,
    px: 4,
    py: 2.5,
    fontSize: 'sm',
    color: isActive ? 'link_accent' : 'text_primary',
    _hover: { bg: hoverBg, color: 'text_primary', textDecoration: 'none' as const },
    borderRadius: 'base',
  };

  const content = (
    <>
      { iconName && <IconSvg name={ iconName } boxSize={ 5 } flexShrink={ 0 } color="text_muted"/> }
      <Text as="span" flex={ 1 }>{ item.text }</Text>
      { showBeta && (
        <Text as="span" fontSize="xs" fontWeight={ 600 } color="link_accent" bg="link_accent_alpha_10" px={ 1.5 } py={ 0.5 } borderRadius="sm">
          Beta
        </Text>
      ) }
    </>
  );

  if (isInternal) {
    return <LinkInternal href={ href } { ...linkProps }>{ content }</LinkInternal>;
  }
  return (
    <Link href={ href } target="_blank" rel="noopener noreferrer" { ...linkProps }>
      { content }
    </Link>
  );
}

function MegaMenuContent({ groups }: { groups: Array<Array<NavItem>> }) {
  return (
    <Flex>
      <Box
        w="300px"
        flexShrink={ 0 }
        p={ 5 }
        pr={ 4 }
        ml={ 1.5 }
        // borderRight="1px solid"
        // borderColor="card_border"
        backgroundColor="surface_muted"
        borderRadius="12px"
      >
        <Text fontSize="lg" fontWeight={ 600 } color="text_primary" mb={ 2 }>
          Tools &amp; Services
        </Text>
        <Text fontSize="sm" color="text_muted" lineHeight="tall" mb={ 4 }>
          Discover more of our tools and services in one place.
        </Text>
        <Text fontSize="sm" color="text_muted" mb={ 2 }>
          Sponsored
        </Text>
        <VStack align="stretch" spacing={ 1 }>
          <MegaMenuRow item={{ text: 'Community', url: '/', icon: 'social/discord' } as MegaMenuItem}/>
        </VStack>
      </Box>
      <Grid
        templateColumns="repeat(3, 1fr)"
        flex={ 1 }
        minW={ 0 }
        py={ 4 }
        px={ 2 }
      >
        { groups.slice(0, 3).map((items, colIndex) => (
          <Box key={ MEGA_SECTION_TITLES[colIndex] }>
            <Text fontSize="sm" fontWeight={ 600 } color="text_muted" px={ 4 } py={ 2 } textTransform="uppercase" letterSpacing="wider">
              { MEGA_SECTION_TITLES[colIndex] }
            </Text>
            <VStack align="stretch" spacing={ 0 }>
              { items.map((navItem) => (
                <MegaMenuRow key={ navItem.text } item={ navItem }/>
              )) }
            </VStack>
          </Box>
        )) }
      </Grid>
    </Flex>
  );
}

interface Props {
  item: NavGroupItem;
}

const NavLinkGroup = ({ item }: Props) => {
  const bgColor = 'transparent';
  const color = item.isActive ? 'link_accent' : 'text_primary';

  const isHighlighted = checkRouteHighlight(item.subItems);
  const hasGroups = item.subItems.some((subItem) => Array.isArray(subItem));
  const isMoreMenu = item.text === 'More' && hasGroups;

  // Mega menu: same width as header content (logo to sign in), centered
  const moreMenuModifiers = React.useMemo(() => [
    {
      name: 'moreMenuLogoToSignIn',
      enabled: true,
      phase: 'beforeWrite' as const,
      fn: ({ state }: { state: Record<string, unknown> }) => {
        const s = state as {
          styles: { popper: Record<string, string> };
          rects: { reference: Record<string, number> };
        };
        const popper = s.styles?.popper;
        if (!popper) return;
        const widthPx = `${ CONTENT_MAX_WIDTH }px`;
        popper.width = widthPx;
        popper.maxWidth = widthPx;
        // Position below full header (top bar + nav) so DSC Price, Gas, logo, and Sign In stay visible
        popper.top = `${ HEADER_HEIGHT_PX }px`;
        popper.transform = 'translate3d(0, 0, 0)';
        if (typeof window !== 'undefined') {
          const leftPx = (window.innerWidth - CONTENT_MAX_WIDTH) / 2;
          popper.left = `${ leftPx }px`;
        }
      },
    },
  ], []);

  return (
    <Popover
      trigger="hover"
      placement="bottom"
      isLazy
      gutter={ 8 }
      modifiers={ isMoreMenu ? moreMenuModifiers : undefined }
    >
      { ({ isOpen }) => (
        <>
          <PopoverTrigger>
            <chakra.li
              className="NavLinkGroup-trigger"
              listStyleType="none"
              display="flex"
              alignItems="center"
              gap="4px"
              px={ 3 }
              py={ 2 }
              fontSize="14px"
              lineHeight={ 5 }
              fontWeight={ item.isActive ? 500 : 400 }
              cursor="pointer"
              color={ isOpen ? 'link_accent' : color }
              _hover={{ color: 'link_accent' }}
              bgColor={ bgColor }
              borderRadius="base"
              { ...getDefaultTransitionProps() }
            >
              { item.text }
              <IconSvg name="arrows/east-mini" boxSize={ 4 } transform="rotate(-90deg)" flexShrink={ 0 }/>
              { isHighlighted && <LightningLabel iconColor={ bgColor } position={{ lg: 'static' }} ml={{ lg: '2px' }}/> }
            </chakra.li>
          </PopoverTrigger>
          <PopoverContent
            className={ isMoreMenu ? 'NavLinkGroup-dropdown NavLinkGroup-mega' : 'NavLinkGroup-dropdown' }
            w={ isMoreMenu ? `${ CONTENT_MAX_WIDTH }px` : 'fit-content' }
            minW={ isMoreMenu ? `${ CONTENT_MAX_WIDTH }px` : '220px' }
            maxW={ isMoreMenu ? `${ CONTENT_MAX_WIDTH }px` : undefined }
            zIndex={ isMoreMenu ? 1018 : undefined }
            bg="card_bg"
            border="1px solid"
            borderColor="card_border"
            borderTopWidth="3px"
            borderTopColor="link_accent"
            borderRadius="sm"
            boxShadow="card"
            overflow="hidden"
          >
            {/* <Box h="3px" minH="3px" bg="link_accent" mt={ -1 } flexShrink={ 0 } aria-hidden/> */}
            <PopoverBody p={ isMoreMenu ? 0 : 2 } py={ isMoreMenu ? 0 : 1 } px={ isMoreMenu ? 0 : 2 } overflow="hidden">
              { (() => {
                if (isMoreMenu) {
                  return <MegaMenuContent groups={ item.subItems as Array<Array<NavItem>> }/>;
                }
                if (hasGroups) {
                  return (
                    <VStack divider={ <StackDivider borderColor="divider"/> } alignItems="stretch" gap={ 0 }>
                      { item.subItems.map((subItem, index) => {
                        if (!Array.isArray(subItem)) {
                          return <NavLink key={ subItem.text } item={ subItem } noIcon submenuItem/>;
                        }
                        return (
                          <chakra.ul key={ index } display="flex" flexDir="column" rowGap={ 0 }>
                            { subItem.map((navItem) => <NavLink key={ navItem.text } item={ navItem } noIcon submenuItem/>) }
                          </chakra.ul>
                        );
                      }) }
                    </VStack>
                  );
                }
                return (
                  <chakra.ul display="flex" flexDir="column" rowGap={ 0 }>
                    { item.subItems.map((subItem) => {
                      if (Array.isArray(subItem)) {
                        return null;
                      }
                      return <NavLink key={ subItem.text } item={ subItem } noIcon submenuItem/>;
                    }) }
                  </chakra.ul>
                );
              })() }
            </PopoverBody>
          </PopoverContent>
        </>
      ) }
    </Popover>
  );
};

export default React.memo(NavLinkGroup);